/**
 * Vancouver-style citation formatter (ICMJE recommendations).
 *
 * Examples
 * --------
 *  6 authors:   "Smith J, Jones K, Patel R, Doe J, Liu M, Brown S."
 *  >6 authors:  "Smith J, Jones K, Patel R, Doe J, Liu M, Brown S, et al."
 *  no authors:  "[No author listed]"
 *
 * Final format (full):
 *   "Smith J, et al. Title of the paper. Journal Name. 2022;doi:10.xxxx/yyyy. PMID: 12345."
 *
 * Falls back gracefully when some bibliographic fields are missing.
 *
 * Input shape (from Citation pydantic model):
 *   { doc_id, source_title, doi, pmid, manubot_ref,
 *     authors: string[], journal, year }
 */
function abbrevAuthor(full) {
  if (!full || typeof full !== 'string') return '';
  // Three input conventions we accept:
  //   1. "Husu, Henrik"          (PubMed long form, comma-separated)
  //   2. "Husu H"                (PubMed short form, already abbreviated)
  //   3. "Henrik Husu"           (Western "first last" convention)
  const initialsRe = /^[A-Z]{1,3}$/;

  // (1) Comma form — split on comma, last name = everything before, given = after.
  if (full.includes(',')) {
    const [last, givenRaw = ''] = full.split(',', 2).map((s) => s.trim());
    if (!last) return '';
    const initials = givenRaw
      .replace(/[.]/g, ' ')
      .split(/\s+/)
      .filter(Boolean)
      .map((p) => p[0].toUpperCase())
      .join('');
    return initials ? `${last} ${initials}` : last;
  }

  const cleaned = full.replace(/[.]/g, ' ').replace(/\s+/g, ' ').trim();
  if (!cleaned) return '';
  const parts = cleaned.split(' ');
  if (parts.length === 1) return parts[0];

  // (2) PubMed short — last token is already initials.
  const last = parts[parts.length - 1];
  if (initialsRe.test(last)) {
    return `${parts.slice(0, -1).join(' ')} ${last}`;
  }

  // (3) Default to Western "First Middle Last" → "Last F[M]"
  const lastName = parts[parts.length - 1];
  const initials = parts.slice(0, -1).map((p) => p[0]?.toUpperCase() || '').join('');
  return `${lastName}${initials ? ' ' + initials : ''}`;
}

export function formatAuthors(authors) {
  if (!Array.isArray(authors) || authors.length === 0) return '';
  const compact = authors.map(abbrevAuthor).filter(Boolean);
  if (compact.length === 0) return '';
  if (compact.length <= 6) return compact.join(', ') + '.';
  return compact.slice(0, 6).join(', ') + ', et al.';
}

/**
 * @param {object} cit - a Citation pydantic record
 * @param {number} [num] - the running citation number (1-based) for the
 *                         leading "[N]" — omit to skip the prefix.
 */
export function formatVancouver(cit, num) {
  if (!cit) return '';
  const parts = [];
  if (typeof num === 'number') parts.push(`[${num}]`);
  const authors = formatAuthors(cit.authors);
  if (authors) parts.push(authors);
  if (cit.source_title) {
    // Ensure title ends with a period
    const t = cit.source_title.trim().replace(/[.;]$/, '');
    parts.push(`${t}.`);
  }
  if (cit.journal) {
    const j = cit.journal.trim().replace(/\.$/, '');
    parts.push(j + (cit.year ? `. ${cit.year}.` : '.'));
  } else if (cit.year) {
    parts.push(`${cit.year}.`);
  }
  if (cit.doi) parts.push(`doi:${cit.doi}.`);
  if (cit.pmid) parts.push(`PMID: ${cit.pmid}.`);
  return parts.join(' ');
}
